import * as React from 'react';

import { faFileLines, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import { useGetListPointsOfInterest } from 'src/features/parkingManagement/api/PointOfInterestsAPIs/useGetListPointsOfInterest';
import { useGetParkingPointOfInterest } from 'src/features/parkingManagement/api/PointOfInterestsAPIs/useGetParkingsPointOfInterest';
import { useUpdateParking } from 'src/features/parkingManagement/api/updateParking';
import PointOfInterestForm from 'src/features/parkingManagement/components/PointsOfInterest/PointOfInterestForm';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
    DialogContent,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

import type { ParkingPointsOfInterest } from 'src/features/parkingManagement/types/parking';

export type CreatePointOfInterestFormSchemaValues = z.infer<typeof createPointOfInterestFormSchema>;
interface AddNewPointOfInterestProps extends DataTableNewEntryProps<ParkingPointsOfInterest> {}

const createPointOfInterestFormSchema = z.object({
    ids: z.array(z.string()),
});

const AddNewPointOfInterest = ({ onSubmit: onSubmitCallback }: AddNewPointOfInterestProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<CreatePointOfInterestFormSchemaValues>({
        resolver: zodResolver(createPointOfInterestFormSchema),
        mode: 'onChange',
        defaultValues: {
            ids: [],
        },
    });

    const { data: listOfPointsOfInterest } = useGetListPointsOfInterest({});
    const { data: parkingPointsOfInterestData, refetch } = useGetParkingPointOfInterest({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const currentPointsOfInterest = React.useMemo(() => {
        return parkingPointsOfInterestData?.point_of_interest_distances.map((item) => item.point_of_interest.id) ?? [];
    }, [parkingPointsOfInterestData?.point_of_interest_distances]);

    const remainingPointsOfInterest = React.useMemo(() => {
        return (listOfPointsOfInterest?.results ?? []).filter(
            (obj: ParkingPointsOfInterest) => !(currentPointsOfInterest ?? []).includes(obj.id),
        );
    }, [currentPointsOfInterest, listOfPointsOfInterest]);

    const { mutate: updateParkingPointsOfInterest } = useUpdateParking({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(parkingManagementMessages.pointOfInterestAddedSuccessfully),
                description: formatMessage(parkingManagementMessages.pointOfInterestAddedSuccessfullyDescription),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    const onSubmit = ({ ids }: { ids: string[] }) => {
        updateParkingPointsOfInterest({
            parkingId: parkingId,
            point_of_interests: [...ids, ...currentPointsOfInterest],
        });
        form.reset();
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button
                    variant="default"
                    className={cn('mb-4 ml-auto')}
                    disabled={remainingPointsOfInterest.length === 0}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {formatMessage(parkingManagementMessages.addNewPoint)}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faFileLines} />
                        <div>
                            <DialogTitle>{formatMessage(parkingManagementMessages.newPointOfInterest)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(parkingManagementMessages.pointsOfInterestSubtitle)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <PointOfInterestForm
                        form={form}
                        onSubmit={form.handleSubmit(onSubmit)}
                        listOfPointsOfInterest={remainingPointsOfInterest}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1" onClick={() => form.reset()}>
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!form.formState.isDirty}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {formatMessage(parkingManagementMessages.newPointOfInterest)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AddNewPointOfInterest;
